import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import AuthScreen from './AuthScreen';
import Settings   from './Settings';
import Contracts  from './Contracts';
import Dashboard  from './Dashboard';
import Ledger     from './Ledger';
import ReportsModal from './ReportsModal';
import { authAPI, invoicesAPI, signatoriesAPI, contractsAPI } from './api';

const CURRENCIES = ['INR','USD','EUR','GBP','AED','SGD','CAD','AUD'];

/* ── Fiscal year & invoice number ───────────── */
const getFiscalYear = () => {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${String(fyStart).slice(-2)}-${String(fyStart + 1).slice(-2)}`;
};
const buildInvoiceNumber = (prefix, seq) => {
  const fy = getFiscalYear();
  return prefix ? `${prefix}/${fy}/${seq}` : `${fy}/${seq}`;
};

/* ── GST calculation ────────────────────────── */
const calcTotals = (items, gstRate, gstType) => {
  const sum  = items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const rate = parseFloat(gstRate || 0);
  if (gstType === 'inclusive') {
    const gst = sum * rate / (100 + rate);
    return { subtotal: sum - gst, gstAmount: gst, total: sum };
  }
  const gst = sum * rate / 100;
  return { subtotal: sum, gstAmount: gst, total: sum + gst };
};

/* ── Number to words (Indian) ───────────────── */
const numberToWords = (n) => {
  if (!n || isNaN(n)) return '';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (x) => {
    if (x === 0) return '';
    if (x < 20)     return ones[x] + ' ';
    if (x < 100)    return tens[Math.floor(x/10)] + (x%10 ? ' '+ones[x%10] : '') + ' ';
    if (x < 1000)   return ones[Math.floor(x/100)] + ' Hundred ' + toW(x%100);
    if (x < 100000) return toW(Math.floor(x/1000)) + 'Thousand ' + toW(x%1000);
    if (x < 10000000) return toW(Math.floor(x/100000)) + 'Lakh ' + toW(x%100000);
    return toW(Math.floor(x/10000000)) + 'Crore ' + toW(x%10000000);
  };
  const int = Math.floor(n), dec = Math.round((n-int)*100);
  let r = toW(int).trim() + ' Rupees';
  if (dec > 0) r += ' and ' + toW(dec).trim() + ' Paise';
  return r + ' Only';
};

const fmt = (n) =>
  parseFloat(n||0).toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 });

const isMeaningfulStatus = (status, excluded = []) => {
  if (!status) return true;
  return !excluded.includes(status);
};

const formatInvoiceLineDescription = (title, details) => {
  const safeTitle = (title || '').trim();
  const safeDetails = (details || '').trim();
  return [safeTitle, safeDetails].filter(Boolean).join('\n');
};

const toDateInputValue = (value) => (value ? String(value).split('T')[0] : '');

const parseInvoiceItems = (items) => {
  if (Array.isArray(items)) {
    return items.map((item, index) => ({
      id: item?.id ?? index + 1,
      description: item?.description || '',
      amount: item?.amount ?? '',
    }));
  }

  if (!items) return [];

  try {
    const parsed = JSON.parse(items);
    return Array.isArray(parsed)
      ? parsed.map((item, index) => ({
          id: item?.id ?? index + 1,
          description: item?.description || '',
          amount: item?.amount ?? '',
        }))
      : [];
  } catch {
    return [];
  }
};

const normalizeInvoiceRecord = (record, contractContext = null) => {
  const fallbackContractId = contractContext?.id || contractContext?.contractId || null;

  return {
    id:              record?.id,
    invoiceNumber:   record?.invoiceNumber || record?.invoice_number || '',
    invoiceDate:     toDateInputValue(record?.invoiceDate || record?.invoice_date),
    clientName:      record?.clientName || record?.client_name || contractContext?.client_name || '',
    clientAddress:   record?.clientAddress || record?.client_address || contractContext?.client_address || '',
    agreementNumber: record?.agreementNumber || record?.agreement_number || contractContext?.contract_number || '',
    kindAttention:   record?.kindAttention || record?.kind_attention || contractContext?.contact_person || '',
    placeOfSupply:   record?.placeOfSupply || record?.place_of_supply || 'Delhi',
    gstRate:         String(record?.gstRate ?? record?.gst_rate ?? 18),
    gstType:         record?.gstType || record?.gst_type || 'exclusive',
    items:           parseInvoiceItems(record?.items),
    signatoryId:     record?.signatoryId || record?.signatory_id || '',
    signatoryName:   record?.signatoryName || record?.signatory_name || record?.sig_name || '',
    signatoryTitle:  record?.signatoryTitle || record?.signatory_title || record?.sig_title || '',
    signatoryImg:    record?.signatoryImg || record?.signature_data || '',
    subtotal:        parseFloat(record?.subtotal || 0),
    gstAmount:       parseFloat(record?.gstAmount || record?.gst_amount || 0),
    total:           parseFloat(record?.total || 0),
    paymentStatus:   record?.paymentStatus || record?.payment_status || 'unpaid',
    paymentDate:     toDateInputValue(record?.paymentDate || record?.payment_date),
    paymentAmount:   parseFloat(record?.paymentAmount || record?.payment_amount || 0),
    paymentMode:     record?.paymentMode || record?.payment_mode || '',
    paymentNotes:    record?.paymentNotes || record?.payment_notes || '',
    tdsAmount:       parseFloat(record?.tdsAmount || record?.tds_amount || 0),
    tdsRate:         parseFloat(record?.tdsRate || record?.tds_rate || 0),
    prefix:          record?.prefix || '',
    currency:        record?.currency || contractContext?.billing_currency || 'INR',
    contractId:      record?.contractId || record?.contract_id || fallbackContractId,
    savedAt:         record?.savedAt || record?.created_at || null,
  };
};

const buildInvoiceItemsFromContract = (contract) => {
  if (!contract) return [{ id: 1, description: '', amount: '' }];

  const milestones = Array.isArray(contract.milestones)
    ? contract.milestones.filter(item => isMeaningfulStatus(item.status, ['cancelled']))
    : [];
  const deliverables = Array.isArray(contract.deliverables)
    ? contract.deliverables.filter(item => isMeaningfulStatus(item.status, ['rejected']))
    : [];

  if (deliverables.length > 0) {
    const deliverablesPerMilestone = deliverables.reduce((acc, item) => {
      if (!item.milestone_id) return acc;
      acc[item.milestone_id] = (acc[item.milestone_id] || 0) + 1;
      return acc;
    }, {});
    const milestoneMap = new Map(milestones.map(item => [String(item.id), item]));

    const items = deliverables.map((item, index) => {
      const linkedMilestone = item.milestone_id ? milestoneMap.get(String(item.milestone_id)) : null;
      const amountValue = linkedMilestone && deliverablesPerMilestone[item.milestone_id] === 1
        ? String(linkedMilestone.amount ?? '')
        : '';
      return {
        id: index + 1,
        description: formatInvoiceLineDescription(item.title, item.description || item.notes),
        amount: amountValue,
      };
    }).filter(item => item.description);

    if (items.length > 0) return items;
  }

  if (milestones.length > 0) {
    const items = milestones.map((item, index) => ({
      id: index + 1,
      description: formatInvoiceLineDescription(item.title, item.description),
      amount: item.amount ? String(item.amount) : '',
    })).filter(item => item.description);
    if (items.length > 0) return items;
  }

  return [{ id: 1, description: contract.project_name || '', amount: '' }];
};

/* ── Invoice Preview ────────────────────────── */
function InvoicePreview({ formData, subtotal, gstAmount, total, firm }) {
  const dateStr = formData.invoiceDate
    ? new Date(formData.invoiceDate).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' })
    : '—';
  const logo = firm?.logoData || null;

  return (
    <div className="preview-shell">
      <div className="preview-toolbar">
        <span className="preview-label">Live Preview</span>
        <button className="print-btn" onClick={() => window.print()}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <polyline points="6 9 6 2 18 2 18 9"/>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <rect x="6" y="14" width="12" height="8"/>
          </svg>
          Print / Save PDF
        </button>
      </div>

      <div className="invoice-paper">
        {/* Header */}
        <div className="inv-header">
          <div className="inv-logo-block">
            {logo
              ? <img src={logo} alt={firm.name} className="invoice-logo-img" />
              : <div className="inv-firm-name-fallback">{firm?.name}</div>
            }
            <div className="inv-company-meta" style={{ marginTop:4 }}>{firm?.address}</div>
            <div className="inv-company-meta">{firm?.phone} &nbsp;|&nbsp; {firm?.email}</div>
          </div>
          <div className="inv-title-block">
            <div className="inv-title">INVOICE</div>
            <div className="inv-number">#{formData.invoiceNumber || '—'}</div>
          </div>
        </div>

        <div className="inv-divider-gold" />

        {/* Meta row */}
        <div className="inv-meta-row">
          <div className="inv-meta-box">
            <div className="inv-meta-label">Invoice Date</div>
            <div className="inv-meta-value">{dateStr}</div>
          </div>
          {formData.agreementNumber && (
            <div className="inv-meta-box">
              <div className="inv-meta-label">Agreement No.</div>
              <div className="inv-meta-value">{formData.agreementNumber}</div>
            </div>
          )}
          <div className="inv-meta-box">
            <div className="inv-meta-label">Place of Supply</div>
            <div className="inv-meta-value">{formData.placeOfSupply || '—'}</div>
          </div>
          <div className="inv-meta-box">
            <div className="inv-meta-label">Tax</div>
            <div className="inv-meta-value">
              {parseFloat(formData.gstRate) > 0
                ? <>{formData.gstRate}% <span className="inv-gst-badge">{formData.gstType === 'inclusive' ? 'Incl.' : 'Excl.'}</span></>
                : <span className="inv-gst-badge inv-gst-nil">Not Applicable</span>
              }
            </div>
          </div>
        </div>

        {/* Bill section */}
        <div className="inv-bill-section">
          <div className="inv-bill-from">
            <div className="inv-bill-title">From</div>
            <div className="inv-bill-name">{firm?.name}</div>
            {firm?.gstNumber && <div className="inv-bill-detail">GST: {firm.gstNumber}</div>}
            {firm?.pan && <div className="inv-bill-detail">PAN: {firm.pan}</div>}
            {firm?.cin && <div className="inv-bill-detail">CIN: {firm.cin}</div>}
          </div>
          <div className="inv-bill-arrow">→</div>
          <div className="inv-bill-to">
            <div className="inv-bill-title">Bill To</div>
            <div className="inv-bill-name">{formData.clientName || <span className="inv-placeholder">Client name</span>}</div>
            {formData.clientAddress && <div className="inv-bill-detail" style={{ whiteSpace:'pre-line' }}>{formData.clientAddress}</div>}
            {formData.kindAttention && <div className="inv-bill-detail"><strong>Attn:</strong> {formData.kindAttention}</div>}
          </div>
        </div>

        {/* Items table */}
        <table className="inv-table">
          <thead>
            <tr>
              <th className="inv-th-sno">S.No.</th>
              <th className="inv-th-desc">Description of Service</th>
              <th className="inv-th-amt">Amount (INR){formData.gstType === 'inclusive' ? ' incl. GST' : ''}</th>
            </tr>
          </thead>
          <tbody>
            {formData.items.map((item, i) => (
              <tr key={item.id}>
                <td className="inv-td-sno">{i+1}</td>
                <td className="inv-td-desc" style={{ whiteSpace:'pre-line' }}>{item.description || <span className="inv-placeholder">—</span>}</td>
                <td className="inv-td-amt">{item.amount ? `₹ ${fmt(item.amount)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="inv-totals">
          <div className="inv-total-row"><span>Taxable Amount</span><span>{fmt(subtotal)}</span></div>
          {parseFloat(formData.gstRate) > 0 && (
            <div className="inv-total-row">
              <span>IGST @ {formData.gstRate}% {formData.gstType === 'inclusive' ? '(included in price)' : ''}</span>
              <span>{fmt(gstAmount)}</span>
            </div>
          )}
          <div className="inv-total-final"><span>Total Payable</span><span>₹ {fmt(total)}</span></div>
          {total > 0 && <div className="inv-words"><strong>Amount in Words:</strong> {numberToWords(total)}</div>}
        </div>

        {/* Bank */}
        <div className="inv-bank">
          <div className="inv-bank-title">Payment Details</div>
          <div className="inv-bank-grid">
            <div><span className="inv-bank-label">Account Name</span><span>{firm?.bankAccountName || firm?.name || '—'}</span></div>
            <div><span className="inv-bank-label">Current Account No.</span><span>{firm?.bankAccount || '—'}</span></div>
            <div><span className="inv-bank-label">Name of Bank and Address</span><span>{[firm?.bankName, firm?.bankBranch].filter(Boolean).join(', ') || firm?.bankName || firm?.bankBranch || '—'}</span></div>
            <div><span className="inv-bank-label">IFSC Code</span><span>{firm?.bankIfsc || '—'}</span></div>
          </div>
        </div>

        {/* Signature */}
        <div className="inv-sign">
          <div className="inv-sign-box">
            {formData.signatoryImg
              ? <img src={formData.signatoryImg} alt="Signature" className="inv-sign-img" />
              : <div className="inv-sign-placeholder" />
            }
            <div className="inv-sign-line" />
            <div className="inv-sign-name">{formData.signatoryName || 'Authorised Signatory'}</div>
            <div className="inv-sign-title">{formData.signatoryTitle || ''}</div>
            <div className="inv-sign-co">For {firm?.name}</div>
          </div>
        </div>
        <div className="inv-footer-note">This is a computer-generated invoice.</div>
      </div>
    </div>
  );
}

/* ── Main App ────────────────────────────────── */
export default function App() {
  /* ── Auth state ── */
  const [auth, setAuth] = useState(() => {
    try {
      const token = localStorage.getItem('ti_token');
      const firm  = JSON.parse(localStorage.getItem('ti_firm')  || 'null');
      const user  = JSON.parse(localStorage.getItem('ti_user')  || 'null');
      return token && firm && user ? { token, firm, user } : null;
    } catch { return null; }
  });

  const [view,          setView]          = useState('dashboard');
  const [showSettings,  setShowSettings]  = useState(false);
  const [showReports,   setShowReports]   = useState(false);
  const [reportModal,   setReportModal]   = useState(null);
  const [toast,         setToast]         = useState(null);
  const [apiStatus,     setApiStatus]     = useState('checking');
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [signatories,    setSignatories]   = useState([]);
  const [contracts,      setContracts]     = useState([]);
  const [activeContract, setActiveContract] = useState(null); // contract linked to current invoice

  /* Lock body scroll when the app shell is active (not on landing page) */
  useEffect(() => {
    if (!auth) return;
    document.body.classList.add('app-scroll-lock');
    return () => document.body.classList.remove('app-scroll-lock');
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    const labels = {
      dashboard: 'Dashboard',
      contracts: 'Contracts',
      ledger: 'Ledger',
    };
    document.title = `e-FinPro | ${showSettings ? 'Settings' : (labels[view] || 'Workspace')}`;
    const robotsTag = document.head.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute('content', 'noindex, nofollow');
    }
  }, [auth, showSettings, view]);

  /* Invoice form state */
  const [invoicePrefix, setInvoicePrefix] = useState(() => auth?.firm?.invoicePrefix || 'INV');
  const [nextItemId,    setNextItemId]    = useState(2);
  const [numLocked,     setNumLocked]     = useState(false);

  const initForm = useCallback(() => ({
    invoiceNumber:   buildInvoiceNumber(auth?.firm?.invoicePrefix || 'INV', '?'),
    invoiceDate:     new Date().toISOString().split('T')[0],
    clientName: '', clientAddress: '', agreementNumber: '', kindAttention: '',
    placeOfSupply:   'Delhi',
    gstRate:         '18',
    gstType:         'exclusive',
    currency:        'INR',
    signatoryId:     '',
    signatoryName:   '',
    signatoryTitle:  '',
    signatoryImg:    '',
    items:           [{ id: 1, description: '', amount: '' }],
  }), [auth]);

  const [formData, setFormData] = useState(initForm);
  const { subtotal, gstAmount, total } = calcTotals(formData.items, formData.gstRate, formData.gstType);

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(() => setToast(null), 2800); };

  const set = useCallback((field, value) => setFormData(p => ({...p,[field]:value})),[]);
  const setItem = useCallback((id,field,value) => setFormData(p => ({...p, items:p.items.map(i => i.id===id ? {...i,[field]:value} : i)})),[]);
  const clearContractLink = useCallback(() => {
    setActiveContract(null);
    setFormData(p => ({ ...p, _contractId: null }));
  }, []);

  /* ── Auth handler ── */
  const handleAuth = (data) => {
    localStorage.setItem('ti_token', data.token);
    localStorage.setItem('ti_firm',  JSON.stringify(data.firm));
    localStorage.setItem('ti_user',  JSON.stringify(data.user));
    setAuth(data);
    setInvoicePrefix(data.firm.invoicePrefix || 'INV');
  };

  const logout = () => {
    localStorage.removeItem('ti_token');
    localStorage.removeItem('ti_firm');
    localStorage.removeItem('ti_user');
    setAuth(null);
    setSavedInvoices([]);
  };

  /* ── Bootstrap: verify token + load data ── */
  useEffect(() => {
    if (!auth?.token) return;
    let cancelled = false;

    (async () => {
      try {
        try {
          const session = await authAPI.me();
          if (!cancelled) {
            localStorage.setItem('ti_firm', JSON.stringify(session.firm));
            localStorage.setItem('ti_user', JSON.stringify(session.user));
            setAuth(prev => {
              if (!prev) return prev;
              const sameFirm = JSON.stringify(prev.firm) === JSON.stringify(session.firm);
              const sameUser = JSON.stringify(prev.user) === JSON.stringify(session.user);
              return sameFirm && sameUser ? prev : { ...prev, firm: session.firm, user: session.user };
            });
          }
        } catch (err) {
          console.warn('Could not refresh authenticated user profile:', err?.message || err);
        }

        await authAPI.health();
        if (!cancelled) setApiStatus('connected');

        try {
          const [rows, sigs, cts] = await Promise.all([invoicesAPI.list(), signatoriesAPI.list(), contractsAPI.list()]);
          if (cancelled) return;
          setSignatories(sigs);
          setContracts(cts);
          setSavedInvoices(normalizeInvoices(rows));
          /* auto-select default signatory on new form */
          const def = sigs.find(s => s.is_default);
          if (def) {
            setFormData(p => ({...p, signatoryId: def.id, signatoryName: def.name, signatoryTitle: def.title||'', signatoryImg: def.signature_data||''}));
          }
        } catch (err) {
          console.warn('Connected to API, but some startup data failed to load:', err?.message || err);
        }
      } catch {
        if (!cancelled) setApiStatus('offline');
      }
    })();
    return () => { cancelled = true; };
  }, [auth?.token]); // eslint-disable-line

  const normalizeInvoices = (rows) => rows.map(r => normalizeInvoiceRecord(r));

  /* ── Invoice number ── */
  const assignNumber = async () => {
    if (numLocked) return;
    let seq = '?';
    if (apiStatus === 'connected') {
      try { seq = (await invoicesAPI.nextSeq(getFiscalYear())).seq; }
      catch { seq = Date.now() % 10000; }
    } else {
      const fy  = getFiscalYear();
      const key = `ti_seq_${auth?.firm?.id}_${fy}`;
      seq = parseInt(localStorage.getItem(key) || '0') + 1;
      localStorage.setItem(key, String(seq));
    }
    setFormData(p => ({...p, invoiceNumber: buildInvoiceNumber(invoicePrefix, seq)}));
    setNumLocked(true);
  };

  const handlePrefixChange = (val) => {
    setInvoicePrefix(val);
    if (!numLocked) setFormData(p => ({...p, invoiceNumber: buildInvoiceNumber(val, '?')}));
  };

  /* ── Signatory select ── */
  const handleSignatorySelect = (id) => {
    const s = signatories.find(x => String(x.id) === String(id));
    if (!s) { set('signatoryId',''); set('signatoryName',''); set('signatoryTitle',''); set('signatoryImg',''); return; }
    setFormData(p => ({...p, signatoryId: s.id, signatoryName: s.name, signatoryTitle: s.title||'', signatoryImg: s.signature_data||''}));
  };

  const ensureContractDetail = async (contract) => {
    if (!contract?.id) return contract;
    if (Array.isArray(contract.deliverables) || Array.isArray(contract.milestones) || Array.isArray(contract.invoices)) {
      return contract;
    }
    try {
      return await contractsAPI.get(contract.id);
    } catch {
      return contract;
    }
  };

  const applyResolvedContractFields = (contract) => {
    if (!contract) return;
    setActiveContract(contract);
    const isIndia = !contract.client_country || contract.client_country === 'India';
    const applyGst = contract.apply_gst !== false;
    const items = buildInvoiceItemsFromContract(contract);
    setFormData(p => ({
      ...p,
      clientName:      contract.client_name     || p.clientName,
      clientAddress:   contract.client_address  || p.clientAddress,
      agreementNumber: contract.contract_number || p.agreementNumber,
      kindAttention:   contract.contact_person  || p.kindAttention,
      placeOfSupply:   isIndia ? (p.placeOfSupply || 'Delhi') : (contract.client_country || p.placeOfSupply),
      gstRate:         applyGst ? (p.gstRate === '0' ? '18' : p.gstRate) : '0',
      currency:        contract.billing_currency || p.currency,
      items,
      _contractId:     contract.id,
    }));
    setNextItemId(items.length + 1);
  };

  /* ── Save invoice ── */
  const saveInvoice = async () => {
    if (!formData.invoiceNumber || formData.invoiceNumber.includes('?')) {
      showToast('Please assign an invoice number first', 'error'); return;
    }
    const payload = {
      invoice_number: formData.invoiceNumber, invoice_date: formData.invoiceDate||null,
      client_name: formData.clientName||null, client_address: formData.clientAddress||null,
      agreement_number: formData.agreementNumber||null, kind_attention: formData.kindAttention||null,
      place_of_supply: formData.placeOfSupply||null,
      gst_rate: formData.gstRate, gst_type: formData.gstType, items: formData.items,
      signatory_id: formData.signatoryId||null, signatory_name: formData.signatoryName||null,
      signatory_title: formData.signatoryTitle||null,
      subtotal, gst_amount: gstAmount, total, prefix: invoicePrefix,
      currency: formData.currency || 'INR',
      contract_id: activeContract?.id || formData._contractId || null,
    };
    if (apiStatus === 'connected') {
      try {
        const saved = await invoicesAPI.save(payload);
        const inv = normalizeInvoices([saved])[0];
        setSavedInvoices(p => [...p.filter(i => i.invoiceNumber !== inv.invoiceNumber), inv]);
        showToast(`Saved to PostgreSQL — ${inv.invoiceNumber}`);
      } catch (err) { showToast(`Error: ${err.message}`, 'error'); }
    } else {
      showToast('Server offline — cannot save', 'error');
    }
  };

  /* ── Load invoice into form ── */
  const loadInvoice = async (inv, contractContext = null) => {
    const cachedInvoice = savedInvoices.find(saved =>
      (saved.id && inv?.id && String(saved.id) === String(inv.id))
      || (saved.invoiceNumber && (saved.invoiceNumber === inv?.invoiceNumber || saved.invoiceNumber === inv?.invoice_number))
    );
    const normalized = normalizeInvoiceRecord(cachedInvoice || inv, contractContext);
    const linkedId = normalized.contractId || contractContext?.id || null;
    const linked = contracts.find(c => String(c.id) === String(linkedId)) || contractContext || null;

    setInvoicePrefix(normalized.prefix || auth?.firm?.invoicePrefix || 'INV');
    setFormData({
      invoiceNumber:   normalized.invoiceNumber,
      invoiceDate:     normalized.invoiceDate || new Date().toISOString().split('T')[0],
      clientName:      normalized.clientName,
      clientAddress:   normalized.clientAddress,
      agreementNumber: normalized.agreementNumber,
      kindAttention:   normalized.kindAttention,
      placeOfSupply:   normalized.placeOfSupply || 'Delhi',
      gstRate:         normalized.gstRate || '18',
      gstType:         normalized.gstType || 'exclusive',
      currency:        normalized.currency || 'INR',
      signatoryId:     normalized.signatoryId || '',
      signatoryName:   normalized.signatoryName || '',
      signatoryTitle:  normalized.signatoryTitle || '',
      signatoryImg:    normalized.signatoryImg || '',
      items:           normalized.items?.length ? normalized.items : [{ id: 1, description: '', amount: '' }],
      _contractId:     linkedId,
    });
    setNumLocked(true);
    setNextItemId((normalized.items?.length || 0) + 2);
    setActiveContract(linked ? await ensureContractDetail(linked) : null);
    setView('form');
    showToast('Invoice loaded into editor');
  };

  const deleteInvoice = async (id) => {
    if (apiStatus === 'connected') {
      try { await invoicesAPI.remove(id); } catch (err) { showToast(`Delete failed: ${err.message}`, 'error'); return; }
    }
    setSavedInvoices(p => p.filter(i => i.id !== id));
    showToast('Invoice deleted', 'error');
  };

  const newInvoice = async (contract = null) => {
    const resolvedContract = await ensureContractDetail(contract);
    const prefix = auth?.firm?.invoicePrefix || 'INV';
    setInvoicePrefix(prefix);
    setActiveContract(resolvedContract || null);
    const def = signatories.find(s => s.is_default);
    const currency = resolvedContract?.billing_currency || 'INR';
    const isIndia  = !resolvedContract || !resolvedContract.client_country || resolvedContract.client_country === 'India';
    const applyGst = resolvedContract ? resolvedContract.apply_gst !== false : true;
    const items = buildInvoiceItemsFromContract(resolvedContract);
    setFormData({
      invoiceNumber:   buildInvoiceNumber(prefix, '?'),
      invoiceDate:     new Date().toISOString().split('T')[0],
      clientName:      resolvedContract?.client_name    || '',
      clientAddress:   resolvedContract?.client_address || '',
      agreementNumber: resolvedContract?.contract_number || '',
      kindAttention:   resolvedContract?.contact_person  || '',
      placeOfSupply:   isIndia ? 'Delhi' : (resolvedContract?.client_country || ''),
      gstRate:         applyGst ? '18' : '0',
      gstType:         'exclusive',
      currency,
      signatoryId:     def?.id    || '',
      signatoryName:   def?.name  || '',
      signatoryTitle:  def?.title || '',
      signatoryImg:    def?.signature_data || '',
      items,
      _contractId:     resolvedContract?.id || null,
    });
    setNumLocked(false); setNextItemId(items.length + 1); setView('form');
  };

  /* Apply contract client fields WITHOUT resetting items or invoice number */
  const applyContractFields = async (contract) => {
    const resolvedContract = await ensureContractDetail(contract);
    applyResolvedContractFields(resolvedContract);
  };

  const exportJSON = () => {
    const a = document.createElement('a');
    a.href = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify({...formData, subtotal, gst_amount: gstAmount, total}, null, 2));
    a.download = `invoice_${formData.invoiceNumber.replace(/\//g,'-')}.json`;
    a.click();
  };

  const handleFirmUpdated = (updated) => {
    const newAuth = {...auth, firm: updated};
    setAuth(newAuth);
    localStorage.setItem('ti_firm', JSON.stringify(updated));
  };

  const openReportModal = (reportKey) => {
    setReportModal(reportKey);
    setShowReports(false);
  };

  /* ── If not logged in, show auth screen ── */
  if (!auth) return <AuthScreen onAuth={handleAuth} />;

  const firm = auth.firm;
  const user = auth.user;

  return (
    <div className="app-root">
      {/* ── Top Nav ── */}
      <nav className="top-nav" onClick={() => showReports && setShowReports(false)}>
        {/* Brand */}
        <div className="top-nav-brand">
          <img src="/efinpro_logo.svg" className="top-nav-logo-img" alt="e-FinPro" />
        </div>
        {/* Centre nav tabs — Dashboard | Contracts | Reports ▼ | Settings */}
        <div className="top-nav-tabs">
          <button className={`nav-tab${view==='dashboard'?' nav-tab-active':''}`} onClick={() => setView('dashboard')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
            Dashboard
          </button>
          <button className={`nav-tab${view==='contracts'?' nav-tab-active':''}`} onClick={() => setView('contracts')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            Contracts
          </button>
          <button className={`nav-tab${view==='ledger'?' nav-tab-active':''}`} onClick={() => setView('ledger')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <rect x="2" y="7" width="20" height="14" rx="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
            Ledger
          </button>

          {/* Reports — stays in the tab bar as a dropdown */}
          <div className="reports-wrap">
            <button className={`nav-tab nav-tab-reports`} onClick={e => { e.stopPropagation(); setShowReports(v => !v); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Reports
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginLeft:2}}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {showReports && (
              <div className="reports-dropdown">
                <div className="reports-dd-title">Download Reports</div>
                {[
                  ['📊', 'Invoice Register', 'CSV · daily, range or FY',  () => openReportModal('invoices')],
                  ['🧾', 'GST Report',       'CSV · filtered taxable invoices', () => openReportModal('gst')],
                  ['🧮', 'TDS Report',       'CSV · payment-date TDS deductions', () => openReportModal('tds')],
                  ['💳', 'Bank Ledger',      'CSV · filtered debit/credit ledger', () => openReportModal('ledger')],
                  ['📁', 'Tally Export',     'XML · filtered vouchers for Tally', () => openReportModal('tally')],
                ].map(([icon, label, sub, fn]) => (
                  <button key={label} className="reports-dd-item" onClick={fn}>
                    <span className="rdi-icon">{icon}</span>
                    <div><div className="rdi-label">{label}</div><div className="rdi-sub">{sub}</div></div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Settings as a tab */}
          {user.role === 'admin' && (
            <button className="nav-tab" onClick={() => setShowSettings(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Settings
            </button>
          )}
        </div>

        {/* Right — DB status + User only */}
        <div className="top-nav-right" onClick={e => e.stopPropagation()}>
          <div className={`api-status api-status-${apiStatus}`} title={apiStatus === 'connected' ? 'Connected to PostgreSQL' : 'Server offline'}>
            <span className="api-dot" />
            <span className="api-status-text">{apiStatus === 'connected' ? 'PostgreSQL' : apiStatus === 'offline' ? 'Offline' : '…'}</span>
          </div>

          {/* User */}
          <div className="top-user-chip">
            <span className="top-user-avatar">{user.name.charAt(0).toUpperCase()}</span>
            <span className="top-user-name">{user.name}</span>
            <button className="top-logout" onClick={logout} title="Sign out">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* ── Form View ── */}
      {view === 'form' && (
        <div className="app">
          <aside className="sidebar">
            <div className="sidebar-header bg-white">
              {firm.logoData
                ? <img src={firm.logoData} alt={firm.name} className="sidebar-logo-img" style={{filter:'none'}} />
                : <span className="sidebar-firm-text">{firm.name}</span>
              }
            </div>

            <div className="form-sections">
              {/* Contract Selector */}
              <div className="fs">
                <div className="fs-head"><span className="fs-dot dot-teal" />Select Contract</div>
                <div className="fg">
                  <label className="lbl">Link to Contract</label>
                  <select
                    className="inp inp-select"
                    value={activeContract?.id ? String(activeContract.id) : ''}
                    onChange={e => {
                      const id = e.target.value;
                      if (!id) { clearContractLink(); return; }
                      const c = contracts.find(x => String(x.id) === id);
                      if (c) applyContractFields(c);
                    }}
                  >
                    <option value="">— No contract (standalone invoice) —</option>
                    {contracts.filter(c => c.status === 'active' || c.status === 'draft').map(c => (
                      <option key={c.id} value={String(c.id)}>
                        {c.project_name} · {c.client_name}{c.contract_number ? ` · #${c.contract_number}` : ''}
                      </option>
                    ))}
                  </select>
                  {contracts.length === 0 && (
                    <div className="hint">No contracts yet — <button type="button" className="hint-link" onClick={() => setView('contracts')}>create one in Contracts</button></div>
                  )}
                </div>
              </div>

              {/* Active Contract Banner */}
              {activeContract && (
                <div className="active-contract-banner">
                  <div className="acb-left">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/>
                      <line x1="8" y1="12" x2="16" y2="12"/>
                    </svg>
                    <div>
                      <div className="acb-name">{activeContract.project_name}</div>
                      <div className="acb-client">{activeContract.client_name}</div>
                    </div>
                  </div>
                  <button className="acb-clear" onClick={clearContractLink} title="Detach contract">×</button>
                </div>
              )}

              {/* Invoice Details */}
              <div className="fs">
                <div className="fs-head"><span className="fs-dot dot-blue" />Invoice Details</div>
                <div className="row2">
                  <div className="fg">
                    <label className="lbl">Prefix</label>
                    <input className="inp" value={invoicePrefix}
                      onChange={e => handlePrefixChange(e.target.value.toUpperCase())}
                      maxLength={8} disabled={numLocked} />
                  </div>
                  <div className="fg">
                    <label className="lbl">Date</label>
                    <input className="inp" type="date" value={formData.invoiceDate} onChange={e => set('invoiceDate', e.target.value)} />
                  </div>
                </div>
                <div className="fg">
                  <label className="lbl">Invoice Number</label>
                  <div className="inp-row">
                    <input className={`inp inp-mono${numLocked?' inp-locked':''}`}
                      value={formData.invoiceNumber}
                      onChange={e => !numLocked && set('invoiceNumber', e.target.value)}
                      readOnly={numLocked} placeholder="Click Assign →" />
                    <button className={`assign-btn${numLocked?' assigned':''}`} onClick={assignNumber}>
                      {numLocked
                        ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                        : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>
                      }
                      {numLocked ? 'Locked' : 'Assign'}
                    </button>
                  </div>
                  <div className="hint">Auto-sequential per firm &amp; fiscal year from DB</div>
                </div>
              </div>

              {/* Client Details */}
              <div className="fs">
                <div className="fs-head"><span className="fs-dot dot-green" />Client Details</div>
                <div className="fg"><label className="lbl">Client Name</label>
                  <input className="inp" value={formData.clientName} onChange={e => set('clientName',e.target.value)} placeholder="Organization or individual" /></div>
                <div className="fg"><label className="lbl">Address</label>
                  <textarea className="inp inp-ta" rows={3} value={formData.clientAddress} onChange={e => set('clientAddress',e.target.value)} placeholder="Full address" /></div>
                <div className="row2">
                  <div className="fg"><label className="lbl">Agreement No.</label>
                    <input className="inp" value={formData.agreementNumber} onChange={e => set('agreementNumber',e.target.value)} /></div>
                  <div className="fg"><label className="lbl">Kind Attention</label>
                    <input className="inp" value={formData.kindAttention} onChange={e => set('kindAttention',e.target.value)} /></div>
                </div>
              </div>

              {/* Line Items */}
              <div className="fs">
                <div className="fs-head-row">
                  <div className="fs-head"><span className="fs-dot dot-amber" />Line Items</div>
                  {activeContract && (
                    <button
                      type="button"
                      className="fs-head-action"
                      onClick={async () => applyResolvedContractFields(await ensureContractDetail(activeContract))}
                    >
                      Use Contract Items
                    </button>
                  )}
                </div>
                {formData.items.map((item, idx) => (
                  <div key={item.id} className="item-block">
                    <div className="item-block-header">
                      <span className="item-idx">#{idx+1}</span>
                      {formData.items.length > 1 && (
                        <button className="rm-btn" onClick={() => setFormData(p => ({...p, items:p.items.filter(i => i.id!==item.id)}))}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </div>
                    <div className="fg"><label className="lbl">Description</label>
                      <textarea className="inp inp-ta" rows={2} value={item.description} onChange={e => setItem(item.id,'description',e.target.value)} placeholder="Service description, period, etc." /></div>
                    <div className="fg"><label className="lbl">Amount (₹){formData.gstType==='inclusive'?' incl. GST':''}</label>
                      <input className="inp" type="number" value={item.amount} onChange={e => setItem(item.id,'amount',e.target.value)} placeholder="0.00" step="0.01" min="0" /></div>
                  </div>
                ))}
                <button className="add-btn" onClick={() => { setFormData(p => ({...p, items:[...p.items,{id:nextItemId,description:'',amount:''}]})); setNextItemId(n=>n+1); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Line Item
                </button>
              </div>

              {/* Tax & Supply */}
              <div className="fs">
                <div className="fs-head"><span className="fs-dot dot-red" />Tax, Currency &amp; Supply</div>
                <div className="row2">
                  <div className="fg"><label className="lbl">Billing Currency</label>
                    <select className="inp inp-select" value={formData.currency || 'INR'} onChange={e => set('currency', e.target.value)}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select></div>
                  <div className="fg"><label className="lbl">Place of Supply</label>
                    <input className="inp" value={formData.placeOfSupply} onChange={e => set('placeOfSupply',e.target.value)} /></div>
                  <div className="fg"><label className="lbl">GST Rate</label>
                    <select className="inp inp-select" value={formData.gstRate} onChange={e => set('gstRate',e.target.value)}>
                      <option value="0">0% — Not Applicable / Exempt</option><option value="5">5%</option>
                      <option value="12">12%</option><option value="18">18% (Standard)</option><option value="28">28%</option>
                    </select></div>
                </div>
                {(formData.currency !== 'INR' || formData.gstRate === '0') && (
                  <div className="tax-note">
                    {formData.currency !== 'INR' ? `⚠ International invoice in ${formData.currency}. ` : ''}
                    {formData.gstRate === '0' ? 'GST not applied.' : ''}
                  </div>
                )}
                <div className="fg"><label className="lbl">GST Calculation</label>
                  <div className="gst-type-toggle">
                    {[['exclusive','Exclusive','GST added on top'],['inclusive','Inclusive','GST inside price']].map(([v,l,d]) => (
                      <button key={v} className={`gst-opt${formData.gstType===v?' gst-opt-active':''}`} onClick={() => set('gstType',v)}>
                        <span className="gst-opt-dot" />
                        <div><div className="gst-opt-label">{l}</div><div className="gst-opt-desc">{d}</div></div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="totals-mini">
                  <div className="tm-row"><span>Taxable</span><span>₹ {subtotal.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                  {parseFloat(formData.gstRate) > 0 && (
                    <div className="tm-row"><span>IGST {formData.gstRate}%</span><span>₹ {gstAmount.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                  )}
                  <div className="tm-total"><span>Total</span><span>₹ {total.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                </div>
              </div>

              {/* Signatory — dropdown from firm's signatories */}
              <div className="fs">
                <div className="fs-head"><span className="fs-dot dot-purple" />Signatory</div>
                <div className="fg">
                  <label className="lbl">Select Authorised Signatory</label>
                  <select className="inp inp-select" value={formData.signatoryId || ''} onChange={e => handleSignatorySelect(e.target.value)}>
                    <option value="">— Select signatory —</option>
                    {signatories.map(s => (
                      <option key={s.id} value={s.id}>{s.name}{s.title ? ` (${s.title})` : ''}{s.is_default ? ' ★' : ''}</option>
                    ))}
                  </select>
                  {signatories.length === 0 && <div className="hint">No signatories yet — add them in <button type="button" className="hint-link" onClick={() => setShowSettings(true)}>Settings → Signatories</button></div>}
                </div>
                {formData.signatoryImg && (
                  <div className="fg">
                    <label className="lbl">Preview</label>
                    <div className="sig-preview-wrap" style={{background:'white'}}>
                      <img src={formData.signatoryImg} alt="sig" className="sig-preview" />
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="action-row">
                <button className="btn-export" onClick={exportJSON}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Export JSON
                </button>
                <button className="btn-print" onClick={() => window.print()}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  Print
                </button>
              </div>
              <button className="btn-save" onClick={saveInvoice}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                Save Invoice
              </button>
            </div>
          </aside>

          <main className="preview-area">
            <InvoicePreview formData={formData} subtotal={subtotal} gstAmount={gstAmount} total={total} firm={firm} />
          </main>
        </div>
      )}

      {/* ── Ledger View ── */}
      {view === 'ledger' && (
        <div className="full-page-view">
          <Ledger />
        </div>
      )}

      {/* ── Contracts View ── */}
      {view === 'contracts' && (
        <div className="full-page-view">
          <Contracts onNewInvoice={(contract) => newInvoice(contract)} onLoadInvoice={loadInvoice} />
        </div>
      )}

      {/* ── Dashboard View ── */}
      {view === 'dashboard' && (
        <div className="full-page-view">
          <Dashboard
            onNewInvoice={(contract) => newInvoice(contract)}
            onLoadInvoice={loadInvoice}
            onOpenContracts={() => setView('contracts')}
          />
        </div>
      )}

      {/* ── Settings overlay ── */}
      {showSettings && (
        <Settings firm={firm} user={user} onClose={() => setShowSettings(false)} onFirmUpdated={handleFirmUpdated} />
      )}

      {/* ── Reports modal ── */}
      {reportModal && (
        <ReportsModal
          firm={firm}
          initialReport={reportModal}
          onClose={() => setReportModal(null)}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.type==='success'
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          }
          {toast.msg}
        </div>
      )}
    </div>
  );
}
